/**
 * Customer 360 Detail Page.
 *
 * Comprehensive customer view aggregating: overview with health score and
 * segment, contacts, equipment owned, deals, orders, CRM activity timeline,
 * certificates, referrals, and notes. Powers the complete customer
 * intelligence experience.
 */

import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  UserCircle,
  Phone,
  Mail,
  Globe,
  Heart,
  Target,
  ShoppingCart,
  ClipboardList,
  FileCheck,
  Users,
  Wrench,
  Plus,
  Star,
  TrendingUp,
  NotebookPen,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import {
  useCRMActivities,
  useCustomerEquipment,
  useCreateActivity,
  useCustomerScore,
  useScoreCustomers,
  useReferrals,
} from "@/features/crm/api";
import { useActivityFacets, useEntityTimeline } from "@/features/activity/api";
import TimelineView from "@/features/activity/components/TimelineView";
import type { Customer, TaxCertificate } from "@/types/sales-order";
import type { PaginatedResponse } from "@/types/common";
import ActionDrawer from "@/components/ui/ActionDrawer";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import ComposeEmail from "@/components/ui/ComposeEmail";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

const SEGMENT_COLORS: Record<string, string> = {
  champion:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
  loyal: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300",
  at_risk: "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300",
  dormant: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  new: "bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300",
  high_value:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
  prospect: "bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300",
};

type Tab =
  | "overview"
  | "contacts"
  | "equipment"
  | "deals"
  | "orders"
  | "activity"
  | "certificates"
  | "referrals";

function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: customer, isLoading } = useQuery<Customer>({
    queryKey: ["customer", id],
    queryFn: async () => (await apiClient.get(`/api/v1/customers/${id}`)).data,
    enabled: !!id,
  });

  const { data: score } = useCustomerScore(id || "");
  const { data: equipment } = useCustomerEquipment(id || "");
  const { data: activitiesData } = useCRMActivities({ customer_id: id });
  const { data: referrals } = useReferrals(id);
  const { data: certs } = useQuery<TaxCertificate[]>({
    queryKey: ["tax-certificates", id],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/tax-certificates?customer_id=${id}`)).data,
    enabled: !!id,
  });
  const { data: dealsData } = useQuery<
    PaginatedResponse<Record<string, unknown>>
  >({
    queryKey: ["customer-deals", id],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/deals?customer_id=${id}`)).data,
    enabled: !!id,
  });
  const { data: ordersData } = useQuery<
    PaginatedResponse<Record<string, unknown>>
  >({
    queryKey: ["customer-orders", id],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/sales-orders?customer_id=${id}`)).data,
    enabled: !!id,
  });

  // FS-058: the audit trail and the contact log are one timeline. This page
  // used to show only the CRM activities, with its own icon map and its own
  // rendering, while the audit feed had no customers in it at all — two
  // timelines for one question.
  const { data: timeline } = useEntityTimeline("customer", id);
  const { data: historyFacets } = useActivityFacets();

  const scoreMutation = useScoreCustomers();
  const createActivityMutation = useCreateActivity();

  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [showEmail, setShowEmail] = useState(false);
  const [showLogActivity, setShowLogActivity] = useState(false);
  const [actType, setActType] = useState("call");
  const [actSubject, setActSubject] = useState("");
  const [actDesc, setActDesc] = useState("");
  const [actOutcome, setActOutcome] = useState("");

  const fmt = (v: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(v);

  if (isLoading) return <LoadingSpinner className="py-24" size="lg" />;
  if (!customer)
    return (
      <div className="py-24 text-center">
        <p className="text-[var(--text-secondary)]">Customer not found.</p>
      </div>
    );

  const activities = activitiesData?.items || [];
  const deals = (dealsData?.items || []) as Array<Record<string, unknown>>;
  const orders = (ordersData?.items || []) as Array<Record<string, unknown>>;

  const tabs: {
    key: Tab;
    label: string;
    icon: React.ElementType;
    count?: number;
  }[] = [
    { key: "overview", label: "Overview", icon: UserCircle },
    {
      key: "contacts",
      label: "Contacts",
      icon: Users,
      count: customer.contacts?.length || 0,
    },
    {
      key: "equipment",
      label: "Equipment",
      icon: Wrench,
      count: equipment?.length || 0,
    },
    { key: "deals", label: "Deals", icon: Target, count: deals.length },
    {
      key: "orders",
      label: "Orders",
      icon: ShoppingCart,
      count: orders.length,
    },
    {
      key: "activity",
      label: "History",
      icon: ClipboardList,
      count: timeline?.total ?? activities.length,
    },
    {
      key: "certificates",
      label: "Certificates",
      icon: FileCheck,
      count: certs?.length || 0,
    },
    {
      key: "referrals",
      label: "Referrals",
      icon: Heart,
      count: referrals?.length || 0,
    },
  ];

  const handleLogActivity = async (e: React.FormEvent) => {
    e.preventDefault();
    await createActivityMutation.mutateAsync({
      activity_type: actType,
      subject: actSubject,
      description: actDesc || undefined,
      outcome: actOutcome || undefined,
      customer_id: id,
      is_completed: true,
    });
    setShowLogActivity(false);
    setActSubject("");
    setActDesc("");
    setActOutcome("");
    toast.success("Activity logged");
  };

  return (
    <div className="page-content space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate("/sales?tab=customers")}
            className="mt-1 text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-2xl font-bold text-[var(--text-primary)]">
                {customer.name}
              </h2>
              {customer.company_name && (
                <span className="text-sm text-[var(--text-tertiary)]">
                  ({customer.company_name})
                </span>
              )}
              {score?.segment && (
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${SEGMENT_COLORS[score.segment] || "bg-slate-100 text-slate-600"}`}
                >
                  {score.segment.replace("_", " ")}
                </span>
              )}
              {customer.tax_exempt && (
                <Badge variant="warning">Tax Exempt</Badge>
              )}
            </div>
            <div className="mt-1 flex items-center gap-4 text-sm text-[var(--text-secondary)] flex-wrap">
              {customer.email && (
                <span className="flex items-center gap-1">
                  <Mail className="h-3.5 w-3.5" />
                  {customer.email}
                </span>
              )}
              {customer.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="h-3.5 w-3.5" />
                  {customer.phone}
                </span>
              )}
              {customer.website && (
                <span className="flex items-center gap-1">
                  <Globe className="h-3.5 w-3.5" />
                  {customer.website}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setShowLogActivity(!showLogActivity);
            }}
          >
            <Plus className="h-3.5 w-3.5" /> Log Activity
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => navigate(`/sales/new?customer_id=${id}`)}
          >
            <ShoppingCart className="h-3.5 w-3.5" /> Create Order
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => scoreMutation.mutate(id)}
          >
            <TrendingUp className="h-3.5 w-3.5" /> Refresh Score
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowEmail(true)}
          >
            <Mail className="h-3.5 w-3.5" /> Send Email
          </Button>
        </div>
      </div>

      {/* Log Activity Drawer */}
      <ActionDrawer
        isOpen={showLogActivity}
        onClose={() => setShowLogActivity(false)}
        title="Log Activity"
        description={`Record an interaction with ${customer.name}.`}
        icon={NotebookPen}
        size="sm"
      >
        <form onSubmit={handleLogActivity} className="space-y-4">
          <div>
            <label className="block text-[13px] font-medium text-[var(--text-primary)]">
              Activity Type
            </label>
            <select
              value={actType}
              onChange={(e) => setActType(e.target.value)}
              className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
            >
              {[
                "call",
                "email",
                "meeting",
                "site_visit",
                "demo",
                "text",
                "note",
                "follow_up",
                "trade_show",
                "other",
              ].map((t) => (
                <option key={t} value={t}>
                  {t.replace("_", " ")}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="Subject"
            value={actSubject}
            onChange={(e) => setActSubject(e.target.value)}
            required
            placeholder="Discussed Q3 order"
          />
          <Input
            label="Notes"
            value={actDesc}
            onChange={(e) => setActDesc(e.target.value)}
            placeholder="Optional details"
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowLogActivity(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              isLoading={createActivityMutation.isPending}
            >
              Log Activity
            </Button>
          </div>
        </form>
      </ActionDrawer>

      {/* Tab navigation */}
      <div className="border-b border-[var(--border-default)]">
        <nav className="flex gap-1 overflow-x-auto pb-px scrollbar-hide">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 shrink-0 border-b-2 px-3 pb-3 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? "border-brand-600 text-brand-600"
                  : "border-transparent text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className="ml-1 text-[10px] bg-[var(--surface-sunken)] rounded-full px-1.5 py-0.5">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Metrics cards */}
          <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card padding="sm">
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                Health Score
              </p>
              <p className="text-2xl font-bold text-[var(--text-primary)] mt-1">
                {score?.health_score ?? "—"}
                <span className="text-sm font-normal text-[var(--text-tertiary)]">
                  /100
                </span>
              </p>
            </Card>
            <Card padding="sm">
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                Lifetime Value
              </p>
              <p className="text-2xl font-bold text-[var(--text-success)] mt-1">
                {score ? fmt(score.lifetime_value) : "—"}
              </p>
            </Card>
            <Card padding="sm">
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                Total Orders
              </p>
              <p className="text-2xl font-bold text-[var(--text-primary)] mt-1">
                {score?.total_orders ?? 0}
              </p>
            </Card>
            <Card padding="sm">
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                RFM Score
              </p>
              <p className="text-2xl font-bold text-[var(--text-primary)] mt-1">
                {score?.rfm_composite_score ?? "—"}
                <span className="text-sm font-normal text-[var(--text-tertiary)]">
                  /15
                </span>
              </p>
            </Card>
            <Card padding="sm">
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                First Purchase
              </p>
              <p className="text-sm font-medium text-[var(--text-primary)] mt-1">
                {score?.first_purchase_date
                  ? new Date(score.first_purchase_date).toLocaleDateString()
                  : "None"}
              </p>
            </Card>
            <Card padding="sm">
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                Last Purchase
              </p>
              <p className="text-sm font-medium text-[var(--text-primary)] mt-1">
                {score?.last_purchase_date
                  ? new Date(score.last_purchase_date).toLocaleDateString()
                  : "None"}
              </p>
            </Card>
            <Card padding="sm">
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                Avg Cycle
              </p>
              <p className="text-sm font-medium text-[var(--text-primary)] mt-1">
                {score?.avg_purchase_cycle_days
                  ? `${score.avg_purchase_cycle_days} days`
                  : "—"}
              </p>
            </Card>
            <Card padding="sm">
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                Next Purchase
              </p>
              <p className="text-sm font-medium text-[var(--text-primary)] mt-1">
                {score?.next_predicted_purchase
                  ? new Date(score.next_predicted_purchase).toLocaleDateString()
                  : "—"}
              </p>
            </Card>
          </div>

          {/* Customer details sidebar */}
          <Card>
            <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">
              Details
            </h3>
            <div className="space-y-2 text-sm">
              {customer.company_name && (
                <div>
                  <span className="text-[var(--text-tertiary)]">Company:</span>{" "}
                  <span className="text-[var(--text-primary)]">
                    {customer.company_name}
                  </span>
                </div>
              )}
              {customer.payment_terms && (
                <div>
                  <span className="text-[var(--text-tertiary)]">Terms:</span>{" "}
                  <span className="text-[var(--text-primary)]">
                    {customer.payment_terms}
                  </span>
                </div>
              )}
              {customer.tax_id && (
                <div>
                  <span className="text-[var(--text-tertiary)]">Tax ID:</span>{" "}
                  <span className="text-[var(--text-primary)]">
                    {customer.tax_id}
                  </span>
                </div>
              )}
              {customer.default_tax_state && (
                <div>
                  <span className="text-[var(--text-tertiary)]">State:</span>{" "}
                  <span className="text-[var(--text-primary)]">
                    {customer.default_tax_state}
                  </span>
                </div>
              )}
              {customer.tax_tags && customer.tax_tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {customer.tax_tags.map((t) => (
                    <span
                      key={t}
                      className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
              {customer.notes && (
                <div className="mt-3 pt-3 border-t border-[var(--border-subtle)]">
                  <p className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap">
                    {customer.notes}
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Recent history — the same timeline as the History tab, trimmed.
              One renderer, so the overview can never drift from the tab. */}
          <div className="lg:col-span-3">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                Recent history
              </h3>
              {(timeline?.total ?? 0) > 5 && (
                <button
                  type="button"
                  onClick={() => setActiveTab("activity")}
                  className="text-xs font-medium text-brand-600 hover:text-brand-700"
                >
                  See all {timeline?.total}
                </button>
              )}
            </div>
            <Card padding="none">
              <div className="py-1">
                <TimelineView
                  entries={(timeline?.items ?? []).slice(0, 5)}
                  emptyMessage="No history yet. Log a call, email, or meeting above."
                />
              </div>
            </Card>
          </div>
        </div>
      )}

      {activeTab === "contacts" && (
        <div className="space-y-3">
          {customer.contacts && customer.contacts.length > 0 ? (
            customer.contacts.map((c) => (
              <Card key={c.id} padding="sm">
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${c.is_primary ? "bg-brand-200 text-brand-700" : "bg-[var(--surface-sunken)] text-[var(--text-secondary)]"}`}
                  >
                    {c.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-[var(--text-primary)]">
                        {c.name}
                      </span>
                      {c.is_primary && (
                        <span className="text-[10px] text-brand-600">
                          <Star className="h-3 w-3 inline fill-current" />{" "}
                          Primary
                        </span>
                      )}
                      {c.title && (
                        <span className="text-xs text-[var(--text-tertiary)]">
                          · {c.title}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-3 text-xs text-[var(--text-tertiary)]">
                      {c.email && (
                        <span>
                          <Mail className="h-3 w-3 inline" /> {c.email}
                        </span>
                      )}
                      {c.phone && (
                        <span>
                          <Phone className="h-3 w-3 inline" /> {c.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <p className="text-sm text-[var(--text-tertiary)] py-8 text-center">
              No contacts on file.
            </p>
          )}
        </div>
      )}

      {activeTab === "equipment" && (
        <div className="space-y-3">
          {equipment && equipment.length > 0 ? (
            equipment.map((e) => (
              <Card key={e.id} padding="sm">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                    <Wrench className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-[var(--text-primary)]">
                        {e.year} {e.make} {e.model}
                      </span>
                      <Badge
                        variant={e.status === "active" ? "success" : "default"}
                      >
                        {e.status}
                      </Badge>
                      {e.category && <Badge variant="info">{e.category}</Badge>}
                    </div>
                    <div className="text-xs text-[var(--text-tertiary)] mt-0.5 flex gap-3">
                      {e.serial_number && <span>SN: {e.serial_number}</span>}
                      {e.hours != null && <span>{e.hours} hrs</span>}
                      {e.mileage != null && (
                        <span>{e.mileage.toLocaleString()} mi</span>
                      )}
                      {e.purchase_date && (
                        <span>
                          Purchased:{" "}
                          {new Date(e.purchase_date).toLocaleDateString()}
                        </span>
                      )}
                      <span className="capitalize">
                        {e.purchase_source.replace("_", " ")}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <p className="text-sm text-[var(--text-tertiary)] py-8 text-center">
              No equipment tracked for this customer.
            </p>
          )}
        </div>
      )}

      {activeTab === "deals" && (
        <div className="space-y-3">
          {deals.length > 0 ? (
            deals.map((d) => (
              <Card key={d.id as string} padding="sm" className="hover-lift">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-[var(--text-primary)]">
                      {String(d.title)}
                    </span>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant="info">
                        {String(d.stage).replace("_", " ")}
                      </Badge>
                      {!!d.source && (
                        <span className="text-[10px] text-[var(--text-tertiary)]">
                          Source: {String(d.source)}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-bold text-[var(--text-primary)]">
                    {fmt(Number(d.estimated_value))}
                  </span>
                </div>
              </Card>
            ))
          ) : (
            <p className="text-sm text-[var(--text-tertiary)] py-8 text-center">
              No deals for this customer.
            </p>
          )}
        </div>
      )}

      {activeTab === "orders" && (
        <div className="space-y-3">
          {orders.length > 0 ? (
            orders.map((o) => (
              <Link key={o.id as string} to={`/sales/${o.id}`}>
                <Card padding="sm" className="hover-lift">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-brand-600">
                        {o.so_number as string}
                      </span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge
                          variant={
                            o.status === "Paid"
                              ? "success"
                              : o.status === "Confirmed"
                                ? "info"
                                : "default"
                          }
                        >
                          {String(o.status)}
                        </Badge>
                        {!!o.order_date && (
                          <span className="text-[10px] text-[var(--text-tertiary)]">
                            {new Date(
                              String(o.order_date),
                            ).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-sm font-bold text-[var(--text-primary)]">
                      {fmt(Number(o.total))}
                    </span>
                  </div>
                </Card>
              </Link>
            ))
          ) : (
            <p className="text-sm text-[var(--text-tertiary)] py-8 text-center">
              No orders for this customer.
            </p>
          )}
        </div>
      )}

      {activeTab === "activity" && (
        <Card padding="none">
          <div className="py-2">
            <TimelineView
              entries={timeline?.items ?? []}
              kinds={historyFacets?.entity_types}
              counted={timeline?.counted}
              emptyMessage="Nothing recorded for this customer yet."
            />
          </div>
        </Card>
      )}

      {activeTab === "certificates" && (
        <div className="space-y-3">
          {certs && certs.length > 0 ? (
            certs.map((cert) => {
              const isExpired =
                cert.expiration_date &&
                new Date(cert.expiration_date) < new Date();
              return (
                <Card key={cert.id} padding="sm">
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${cert.is_revoked || isExpired ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"}`}
                    >
                      <FileCheck className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[var(--text-primary)]">
                          {cert.certificate_type}
                        </span>
                        {cert.certificate_number && (
                          <span className="text-xs text-[var(--text-tertiary)]">
                            #{cert.certificate_number}
                          </span>
                        )}
                        <Badge variant={cert.is_current ? "success" : "danger"}>
                          {cert.is_current
                            ? "Current"
                            : isExpired
                              ? "Expired"
                              : "Revoked"}
                        </Badge>
                      </div>
                      <div className="text-[11px] text-[var(--text-tertiary)]">
                        {cert.expiration_date && (
                          <span>
                            Expires:{" "}
                            {new Date(
                              cert.expiration_date,
                            ).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })
          ) : (
            <p className="text-sm text-[var(--text-tertiary)] py-8 text-center">
              No certificates on file.
            </p>
          )}
        </div>
      )}

      {activeTab === "referrals" && (
        <div className="space-y-3">
          {referrals && referrals.length > 0 ? (
            referrals.map((r) => (
              <Card key={r.id} padding="sm">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-[var(--text-primary)]">
                      {r.referred_name}
                    </span>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge
                        variant={
                          r.status === "converted"
                            ? "success"
                            : r.status === "lost"
                              ? "danger"
                              : "default"
                        }
                      >
                        {r.status}
                      </Badge>
                      <span className="text-[10px] text-[var(--text-tertiary)]">
                        {new Date(r.referral_date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  {r.referral_value && (
                    <span className="text-sm font-bold text-[var(--text-success)]">
                      {fmt(r.referral_value)}
                    </span>
                  )}
                </div>
              </Card>
            ))
          ) : (
            <p className="text-sm text-[var(--text-tertiary)] py-8 text-center">
              No referrals from this customer.
            </p>
          )}
        </div>
      )}

      <ComposeEmail
        isOpen={showEmail}
        onClose={() => setShowEmail(false)}
        defaultTo={customer.email || undefined}
        defaultToName={customer.name}
        entityType="customer"
        entityId={id || ""}
      />
    </div>
  );
}

export default CustomerDetailPage;
